// Learning observations data layer.
//
// An observation is a human-authored record of what a teacher noticed. It is
// neither an assessment nor a score. In live HoneyAcorn, role, center,
// classroom, publication, and data-minimization rules live in Postgres RPCs;
// this module only normalizes their explicit projections for the UI.

import { supabase, isSupabaseConfigured } from "./supabase";
import { DEMO_CHILDREN, DEMO_CLASSROOMS } from "./dailyReportsDemo";

const DEMO_ORGANIZATION_ID = "00000000-0000-0000-0000-000000000001";
const DEMO_STORE_KEY = "daycare.demo.learning-observations.v1";

function unwrapRpc(value) {
  return Array.isArray(value) ? value[0] : value;
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

function text(value, fallback = "") {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function numberOr(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function requireText(value, label, maximum = 2000) {
  const result = text(value);
  if (!result) throw new Error(`${label} is required.`);
  if (result.length > maximum) throw new Error(`${label} must be ${maximum} characters or fewer.`);
  return result;
}

function normalizeDateTime(value) {
  if (!value) return new Date().toISOString();
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error("Choose a valid observation time.");
  return date.toISOString();
}

export function learningFocusCode(label) {
  return text(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function normalizeLearningFocus(raw = {}) {
  return {
    id: raw.id || null,
    code: raw.code || null,
    label: text(raw.label, "Focus area"),
    description: text(raw.description) || null,
    isActive: raw.is_active !== false,
    sortOrder: numberOr(raw.sort_order, 100),
    createdAt: raw.created_at || null,
    updatedAt: raw.updated_at || null,
  };
}

function normalizeTeacherObservation(raw = {}) {
  return {
    id: raw.id || null,
    focusId: raw.focus_id || null,
    focusLabel: text(raw.focus_label) || null,
    observedAt: raw.observed_at || null,
    note: text(raw.note),
    familyVisible: Boolean(raw.family_visible),
  };
}

function normalizeAdminObservation(raw = {}) {
  return {
    ...normalizeTeacherObservation(raw),
    childId: raw.child_id || null,
    childName: text(raw.child_name, "Child"),
    classroomId: raw.classroom_id || null,
    classroomName: text(raw.classroom_name, "Classroom"),
    createdByName: text(raw.created_by_name) || null,
    familyVisibleAt: raw.family_visible_at || null,
    createdAt: raw.created_at || null,
  };
}

function normalizeParentObservation(raw = {}) {
  return {
    id: raw.id || null,
    childId: raw.child_id || null,
    focusLabel: text(raw.focus_label) || null,
    observedAt: raw.observed_at || null,
    note: text(raw.note),
    sharedAt: raw.shared_at || null,
  };
}

export function normalizeLearningTeacherSnapshot(raw) {
  const value = unwrapRpc(raw) || {};
  return {
    status: value.status || "ready",
    childId: value.child_id || null,
    classroomId: value.classroom_id || null,
    focuses: list(value.focuses).map(normalizeLearningFocus),
    recentObservations: list(value.recent_observations).map(normalizeTeacherObservation),
  };
}

export function normalizeLearningAdminSnapshot(raw) {
  const value = unwrapRpc(raw) || {};
  return {
    status: value.status || "not_configured",
    windowStart: value.window_start || null,
    windowEnd: value.window_end || null,
    focuses: list(value.focuses).map(normalizeLearningFocus),
    observations: list(value.observations).map(normalizeAdminObservation),
  };
}

function demoDefaultState() {
  // Do not fabricate a curriculum, focus area, or family-facing observation.
  // A demo administrator can deliberately configure one through the same UI.
  return { focuses: [], observations: [], auditEvents: [] };
}

function readDemoState() {
  if (typeof window === "undefined" || !window.localStorage) return demoDefaultState();
  try {
    const stored = window.localStorage.getItem(DEMO_STORE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        focuses: list(parsed.focuses),
        observations: list(parsed.observations),
        auditEvents: list(parsed.auditEvents),
      };
    }
  } catch {
    // A clean temporary demo state is safer than trusting malformed storage.
  }
  const initial = demoDefaultState();
  writeDemoState(initial);
  return initial;
}

function writeDemoState(state) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(DEMO_STORE_KEY, JSON.stringify(state));
  } catch {
    // Keep the current interaction usable when browser storage is unavailable.
  }
}

function demoId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function demoChild(childId) {
  return DEMO_CHILDREN.find((child) => child.id === childId) || null;
}

function demoClassroomName(classroomId) {
  return DEMO_CLASSROOMS.find((classroom) => classroom.id === classroomId)?.name || "Classroom";
}

function demoAdminObservation(observation, state) {
  const child = demoChild(observation.child_id);
  const focus = state.focuses.find((row) => row.id === observation.focus_id) || null;
  return {
    id: observation.id,
    child_id: observation.child_id,
    child_name: child ? [child.first_name, child.last_name].filter(Boolean).join(" ") : "Child",
    classroom_id: observation.classroom_id,
    classroom_name: demoClassroomName(observation.classroom_id),
    focus_id: observation.focus_id,
    focus_label: focus?.label || null,
    observed_at: observation.observed_at,
    note: observation.note,
    created_by_name: observation.created_by_name || "Teacher",
    family_visible: Boolean(observation.family_visible_at),
    family_visible_at: observation.family_visible_at || null,
    created_at: observation.created_at,
  };
}

function focusInput({ label, description = null, isActive = true, sortOrder = 100 }) {
  const nextLabel = requireText(label, "Focus name", 120);
  const nextDescription = text(description) || null;
  if (nextDescription?.length > 1000) throw new Error("Focus description must be 1000 characters or fewer.");
  const nextSortOrder = numberOr(sortOrder, 100);
  if (nextSortOrder < 0 || nextSortOrder > 100000) throw new Error("Focus order must be between 0 and 100000.");
  return {
    label: nextLabel,
    description: nextDescription,
    is_active: Boolean(isActive),
    sort_order: nextSortOrder,
  };
}

export async function getTeacherLearningObservationSnapshot({ childId, actorProfileId = null } = {}) {
  if (!childId) throw new Error("Choose a child first.");
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_learning_teacher_observation_snapshot", {
      p_child_id: childId,
    });
    if (error) throw error;
    return normalizeLearningTeacherSnapshot(data);
  }

  const child = demoChild(childId);
  if (!child) throw new Error("This child is not available in the demo.");
  const state = readDemoState();
  return normalizeLearningTeacherSnapshot({
    status: "ready",
    child_id: child.id,
    classroom_id: child.classroom_id,
    focuses: state.focuses.filter((focus) => focus.is_active),
    recent_observations: state.observations
      .filter((observation) => observation.child_id === child.id)
      .filter((observation) => !actorProfileId || observation.created_by === actorProfileId)
      .sort((a, b) => b.observed_at.localeCompare(a.observed_at))
      .slice(0, 8)
      .map((observation) => ({
        ...observation,
        focus_label: state.focuses.find((focus) => focus.id === observation.focus_id)?.label || null,
        family_visible: Boolean(observation.family_visible_at),
      })),
  });
}

export async function createLearningObservation({
  childId,
  focusId = null,
  observedAt = null,
  note,
  dailyReportId = null,
  actorProfileId = null,
  actorName = null,
} = {}) {
  if (!childId) throw new Error("Choose a child first.");
  const nextNote = requireText(note, "Observation", 2000);
  const nextObservedAt = normalizeDateTime(observedAt);

  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_create_learning_observation", {
      p_child_id: childId,
      p_focus_id: focusId || null,
      p_observed_at: nextObservedAt,
      p_note: nextNote,
      p_daily_report_id: dailyReportId || null,
    });
    if (error) throw error;
    return normalizeTeacherObservation(unwrapRpc(data));
  }

  const child = demoChild(childId);
  if (!child) throw new Error("This child is not available in the demo.");
  const state = readDemoState();
  const focus = focusId ? state.focuses.find((row) => row.id === focusId && row.is_active) : null;
  if (focusId && !focus) throw new Error("This focus is not active in the demo center.");
  const now = new Date().toISOString();
  const observation = {
    id: demoId("demo-learning-observation"),
    organization_id: DEMO_ORGANIZATION_ID,
    child_id: child.id,
    classroom_id: child.classroom_id,
    focus_id: focus?.id || null,
    daily_report_id: dailyReportId || null,
    observed_at: nextObservedAt,
    note: nextNote,
    family_visible_at: null,
    family_visible_by: null,
    created_by: actorProfileId || "demo-teacher",
    created_by_name: text(actorName, "Teacher"),
    created_at: now,
  };
  state.observations.unshift(observation);
  state.auditEvents.unshift({
    id: demoId("demo-learning-audit"),
    observation_id: observation.id,
    event_type: "created",
    created_at: now,
  });
  writeDemoState(state);
  return normalizeTeacherObservation({
    ...observation,
    focus_label: focus?.label || null,
    family_visible: false,
  });
}

export async function getLearningAdminSnapshot({ from = null, to = null, childId = null } = {}) {
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_learning_admin_snapshot", {
      p_from: from || null,
      p_to: to || null,
      p_child_id: childId || null,
    });
    if (error) throw error;
    return normalizeLearningAdminSnapshot(data);
  }

  const state = readDemoState();
  const observations = state.observations
    .filter((observation) => !childId || observation.child_id === childId)
    .filter((observation) => !from || observation.observed_at.slice(0, 10) >= from)
    .filter((observation) => !to || observation.observed_at.slice(0, 10) <= to)
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))
    .slice(0, 200)
    .map((observation) => demoAdminObservation(observation, state));
  return normalizeLearningAdminSnapshot({
    status: state.focuses.length ? "configured" : "not_configured",
    window_start: from || null,
    window_end: to || null,
    focuses: state.focuses,
    observations,
  });
}

export async function upsertLearningFocus({
  focusId = null,
  label,
  description = null,
  isActive = true,
  sortOrder = 100,
} = {}) {
  const input = focusInput({ label, description, isActive, sortOrder });
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_upsert_learning_observation_focus", {
      p_focus_id: focusId || null,
      p_label: input.label,
      p_description: input.description,
      p_is_active: input.is_active,
      p_sort_order: input.sort_order,
    });
    if (error) throw error;
    return normalizeLearningFocus(unwrapRpc(data));
  }

  const state = readDemoState();
  const duplicate = state.focuses.find(
    (focus) => focus.id !== focusId && focus.label.toLocaleLowerCase() === input.label.toLocaleLowerCase(),
  );
  if (duplicate) throw new Error("A focus with this name already exists.");
  const now = new Date().toISOString();
  let focus;
  if (focusId) {
    const current = state.focuses.find((row) => row.id === focusId);
    if (!current) throw new Error("This focus is not available in the demo center.");
    focus = { ...current, ...input, updated_at: now };
    state.focuses = state.focuses.map((row) => (row.id === focusId ? focus : row));
  } else {
    const code = learningFocusCode(input.label);
    if (!code) throw new Error("Focus name must include a letter or number.");
    if (state.focuses.some((row) => row.code === code)) {
      throw new Error("A focus with this name already exists.");
    }
    focus = {
      id: demoId("demo-learning-focus"),
      organization_id: DEMO_ORGANIZATION_ID,
      code,
      ...input,
      created_at: now,
      updated_at: now,
    };
    state.focuses.push(focus);
  }
  writeDemoState(state);
  return normalizeLearningFocus(focus);
}

export async function setLearningObservationFamilyVisibility({ observationId, visible }) {
  if (!observationId) throw new Error("Choose an observation first.");
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_set_learning_observation_family_visibility", {
      p_observation_id: observationId,
      p_visible: Boolean(visible),
    });
    if (error) throw error;
    const value = unwrapRpc(data) || {};
    return {
      id: value.id || observationId,
      familyVisible: Boolean(value.family_visible),
      familyVisibleAt: value.family_visible_at || null,
    };
  }

  const state = readDemoState();
  const current = state.observations.find((row) => row.id === observationId);
  if (!current) throw new Error("This observation is not available in the demo center.");
  const nextVisible = Boolean(visible);
  if (Boolean(current.family_visible_at) !== nextVisible) {
    const now = new Date().toISOString();
    current.family_visible_at = nextVisible ? now : null;
    current.family_visible_by = nextVisible ? "demo-admin" : null;
    state.auditEvents.unshift({
      id: demoId("demo-learning-audit"),
      observation_id: current.id,
      event_type: nextVisible ? "shared_with_family" : "hidden_from_family",
      created_at: now,
    });
    writeDemoState(state);
  }
  return {
    id: current.id,
    familyVisible: Boolean(current.family_visible_at),
    familyVisibleAt: current.family_visible_at || null,
  };
}

export async function listParentLearningObservations({ childId, limit = 30 } = {}) {
  if (!childId) return [];
  if (isSupabaseConfigured) {
    const { data, error } = await supabase.rpc("rpc_parent_learning_observations", {
      p_child_id: childId,
      p_limit: Math.min(Math.max(Number(limit) || 30, 1), 100),
    });
    if (error) throw error;
    // This RPC intentionally returns a JSONB array. Do not unwrap it as if
    // it were a row-returning PostgREST function; doing so would discard all
    // but the first family-visible observation.
    return list(data).map(normalizeParentObservation);
  }

  const state = readDemoState();
  return state.observations
    .filter((observation) => observation.child_id === childId && observation.family_visible_at)
    .sort((a, b) => b.observed_at.localeCompare(a.observed_at))
    .slice(0, Math.min(Math.max(Number(limit) || 30, 1), 100))
    .map((observation) => normalizeParentObservation({
      id: observation.id,
      child_id: observation.child_id,
      focus_label: state.focuses.find((focus) => focus.id === observation.focus_id)?.label || null,
      observed_at: observation.observed_at,
      note: observation.note,
      shared_at: observation.family_visible_at,
    }));
}

export function resetLearningObservationDemoForTests() {
  if (typeof window === "undefined" || !window.localStorage) return;
  window.localStorage.removeItem(DEMO_STORE_KEY);
}
